import React, { useState, useMemo } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { 
  ArrowUpDownIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CopyIcon,
  EyeIcon,
  MinusIcon,
  MoreVerticalIcon,
  PlusIcon,
  SearchIcon
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { EvaluateResponse } from "../../model_gen/evaluation";
import { cn } from "../../lib/utils";
import SmartPagination from "../shared/SmartPagination";
import RequestViewModal from "../Request/RequestViewModal";
import moment from "moment";

interface SortConfig {
  key: keyof EvaluateResponse | null;
  direction: "asc" | "desc";
}

interface EvaluationsTableProps {
  evaluations: EvaluateResponse[];
  totalEvaluations: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  selectedSentinels?: Set<number>;
  onToggleSentinelFilter?: (sentinelId: number, sentinelName: string, status?: string) => void;
  apiKey: ApiKeyOrGetter;
}

const SortableHeader: React.FC<{ 
  children: React.ReactNode; 
  sortKey: keyof EvaluateResponse;
  sortConfig: SortConfig;
  onSort: (key: keyof EvaluateResponse) => void;
  className?: string;
}> = ({ children, sortKey, sortConfig, onSort, className }) => (
  <TableHead className={cn("cursor-pointer hover:bg-accent/20 transition-colors", className)}>
    <button
      onClick={() => onSort(sortKey)}
      className={cn(
        "flex items-center gap-2 font-gc-med text-muted-foreground hover:text-foreground transition-colors",
        sortConfig.key === sortKey && "text-maitai-green"
      )}
    >
      {children}
      <ArrowUpDownIcon className={cn(
        "w-3 h-3 transition-colors",
        sortConfig.key === sortKey && "text-maitai-green"
      )} />
    </button>
  </TableHead>
);

const EvaluationsTable: React.FC<EvaluationsTableProps> = ({
  evaluations,
  totalEvaluations,
  currentPage,
  pageSize,
  isLoading,
  onPageChange,
  selectedSentinels,
  onToggleSentinelFilter,
  apiKey
}) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: "desc" });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [requestModalVisible, setRequestModalVisible] = useState(false);
  const [selectedRequestData, setSelectedRequestData] = useState<{
    requestId: string;
    applicationId: string;
    applicationActionId: string;
  } | null>(null);

  const handleSort = (key: keyof EvaluateResponse) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedEvaluations = useMemo(() => {
    if (!sortConfig.key) return evaluations;
    
    return [...evaluations].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];
      
      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [evaluations, sortConfig]);

  const totalPages = Math.ceil(totalEvaluations / pageSize);

  const formatDate = (timestamp: number | null) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number | null) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  // Determine if evaluation has any faults
  const getEvaluationStatus = (evaluation: EvaluateResponse): "PASS" | "FAULT" => {
    if (!evaluation.evaluation_results || evaluation.evaluation_results.length === 0) {
      return "PASS";
    }
    
    const hasFault = evaluation.evaluation_results.some(result => result.status === "FAULT");
    return hasFault ? "FAULT" : "PASS";
  };

  const getStatusColor = (status: "PASS" | "FAULT") => {
    switch (status) {
      case "PASS":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "FAULT":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      default:
        return "bg-muted text-foreground";
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleViewDetails = (evaluation: EvaluateResponse) => {
    // TODO: Navigate to evaluation details page
    console.log("View evaluation details:", evaluation);
  };

  const handleViewRequest = (evaluation: EvaluateResponse) => {
    if (evaluation.request_id && evaluation.application_id && evaluation.meta?.application_action_id) {
      setSelectedRequestData({
        requestId: String(evaluation.request_id),
        applicationId: String(evaluation.application_id),
        applicationActionId: String(evaluation.meta.application_action_id),
      });
      setRequestModalVisible(true);
    }
  };

  const handleCloseRequestModal = () => {
    setRequestModalVisible(false);
    setSelectedRequestData(null);
  };

  const toggleRowExpansion = (evaluationId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(evaluationId)) {
      newExpandedRows.delete(evaluationId);
    } else {
      newExpandedRows.add(evaluationId);
    }
    setExpandedRows(newExpandedRows);
  };

  const getResultStatusColor = (status: string | null) => {
    switch (status) {
      case "PASS":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "FAULT":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      case "ERROR":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400";
      case "NA":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
      default:
        return "bg-muted text-foreground";
    }
  };

  if (isLoading && evaluations.length === 0) {
    return (
      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border hover:bg-transparent">
                <TableHead className="w-[5%]"></TableHead>
                <TableHead className="w-[18%]">Date Created</TableHead>
                <TableHead className="w-[12%]">Status</TableHead>
                <TableHead className="w-[20%]">Application</TableHead>
                <TableHead className="w-[20%]">Intent</TableHead>
                <TableHead className="w-[15%]">Request ID</TableHead>
                <TableHead className="w-[10%]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i} className="border-b border-border/50">
                  <TableCell className="py-4">
                    <Skeleton className="h-6 w-6 rounded" />
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </TableCell>
                  <TableCell className="py-4">
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell className="py-4">
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-6 w-16 rounded" />
                      <Skeleton className="h-6 w-6 rounded" />
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <Skeleton className="h-8 w-8 rounded" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  if (evaluations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="space-y-3">
          <h3 className="text-lg font-gc-med text-foreground">
            No evaluations found
          </h3>
          <p className="text-muted-foreground max-w-md">
            No evaluation results match your current filters. Try adjusting your filters or check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-border">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border hover:bg-transparent">
              <TableHead className="w-[5%]"></TableHead>
              <SortableHeader 
                sortKey="date_created" 
                sortConfig={sortConfig} 
                onSort={handleSort}
                className="w-[18%]"
              >
                Date Created
              </SortableHeader>
              <TableHead className="w-[12%]">
                <span className="font-gc-med text-muted-foreground">Status</span>
              </TableHead>
              <TableHead className="w-[20%]">
                <span className="font-gc-med text-muted-foreground">Application</span>
              </TableHead>
              <TableHead className="w-[20%]">
                <span className="font-gc-med text-muted-foreground">Intent</span>
              </TableHead>
              <TableHead className="w-[15%]">
                <span className="font-gc-med text-muted-foreground">Request ID</span>
              </TableHead>
              <TableHead className="w-[10%]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedEvaluations.map((evaluation) => {
              const status = getEvaluationStatus(evaluation);
              const faultCount = evaluation.evaluation_results?.filter(r => r.status === "FAULT").length || 0;
              const totalCount = evaluation.evaluation_results?.length || 0;
              const evaluationKey = evaluation.id?.toString() || evaluation.evaluation_request_id || "";
              const isExpanded = expandedRows.has(evaluationKey);
              
              return (
                <React.Fragment key={evaluationKey}>
                  {/* Main Row */}
                  <TableRow className="hover:bg-accent/20 transition-colors border-b border-border/50">
                    <TableCell className="py-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => toggleRowExpansion(evaluationKey)}
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="w-4 h-4" />
                        ) : (
                          <ChevronRightIcon className="w-4 h-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="space-y-1">
                        <div className="text-sm font-gc-reg text-foreground">
                          {formatDate(evaluation.date_created)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTime(evaluation.date_created)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
                          getStatusColor(status)
                        )}>
                          {status}
                        </span>
                        {totalCount > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {totalCount - faultCount}/{totalCount}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/application/${evaluation.application_id}`);
                        }}
                        className="text-sm font-gc-reg text-maitai-green hover:underline text-left"
                      >
                        {String(evaluation.meta?.application_name || `App ${evaluation.application_id}` || "Unknown")}
                      </button>
                    </TableCell>
                    <TableCell className="py-4">
                      {evaluation.meta?.application_action_id ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/application/${evaluation.application_id}/intent/${evaluation.meta.application_action_id}`);
                          }}
                          className="text-sm font-gc-reg text-maitai-green hover:underline text-left"
                        >
                          {String(evaluation.meta?.intent_name || "Unknown Intent")}
                        </button>
                      ) : (
                        <span className="text-sm font-gc-reg text-foreground">
                          {String(evaluation.meta?.intent_name || "Unknown Intent")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
                          ...{evaluation.request_id?.slice(-6) || ""}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(String(evaluation.request_id || ""));
                          }}
                        >
                          <CopyIcon className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center gap-1">
                        {evaluation.request_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1"
                            onClick={() => handleViewRequest(evaluation)}
                          >
                            <SearchIcon className="w-3 h-3" />
                            Request
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <MoreVerticalIcon className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetails(evaluation)}>
                              <EyeIcon className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {evaluation.request_id && (
                              <DropdownMenuItem onClick={() => copyToClipboard(String(evaluation.request_id || ""))}>
                                <CopyIcon className="w-4 h-4 mr-2" />
                                Copy Request ID
                              </DropdownMenuItem>
                            )}
                            {evaluation.evaluation_request_id && (
                              <DropdownMenuItem onClick={() => copyToClipboard(String(evaluation.evaluation_request_id || ""))}>
                                <CopyIcon className="w-4 h-4 mr-2" />
                                Copy Evaluation ID
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Subtable */}
                  {isExpanded && evaluation.evaluation_results && evaluation.evaluation_results.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="py-0 bg-muted/20">
                        <div className="px-4 py-3">
                          <div className="text-sm font-gc-med text-muted-foreground mb-3">
                            Evaluation Results ({evaluation.evaluation_results.length})
                          </div>
                          <div className="space-y-2">
                            {evaluation.evaluation_results.map((result, index) => (
                              <div
                                key={result.id || index}
                                className="flex items-center justify-between p-3 bg-background rounded border border-border/50"
                              >
                                <div className="flex items-center gap-4 flex-1">
                                  <span className={cn(
                                    "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
                                    getResultStatusColor(result.status)
                                  )}>
                                    {result.status || "Unknown"}
                                  </span>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-gc-reg text-foreground">
                                        {result.sentinel_name || "Unknown Sentinel"}
                                      </span>
                                      {result.sentinel_id && onToggleSentinelFilter && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleSentinelFilter(
                                              result.sentinel_id,
                                              result.sentinel_name || "Unknown Sentinel",
                                              result.status || undefined
                                            );
                                          }}
                                          className="opacity-60 hover:opacity-100 transition-opacity p-1 rounded hover:bg-accent"
                                          title={
                                            selectedSentinels?.has(result.sentinel_id)
                                              ? "Remove from filters"
                                              : `Add to filters (${result.status || "Unknown"})`
                                          }
                                        >
                                          {selectedSentinels?.has(result.sentinel_id) ? (
                                            <MinusIcon className="w-3 h-3 text-red-500" />
                                          ) : (
                                            <PlusIcon className="w-3 h-3 text-green-500" />
                                          )}
                                        </button>
                                      )}
                                    </div>
                                    {result.description && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        {result.description}
                                      </div>
                                    )}
                                  </div>
                                  {result.eval_time && (
                                    <div className="text-sm text-muted-foreground">
                                      {result.eval_time}ms
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>

        <SmartPagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalEvaluations / pageSize)}
          onPageChange={onPageChange}
          itemName="evaluations"
          totalItems={totalEvaluations}
          itemsPerPage={pageSize}
        />
      </CardContent>

      <RequestViewModal
        requestId={selectedRequestData?.requestId}
        applicationId={selectedRequestData?.applicationId}
        applicationActionId={selectedRequestData?.applicationActionId}
        isVisible={requestModalVisible}
        onClose={handleCloseRequestModal}
        apiKey={apiKey}
      />
    </Card>
  );
};

export default EvaluationsTable; 